<template>
	<div>
		<div class="stage">
			<page-header title="Porting" />

			<filter-bar
				:show-filters="showFilters"
				:has-export="true"
				:show-add-button="$global.hasUserRole(userRoles, 'numberports:create')"
				add-button-label="Submit Porting Request"
				@toggle-filters="showFilters = !showFilters"
				@reset-filters="resetSearchParams"
				@export="exportToCsv"
				@add-new="handleAddNew"
			>
			</filter-bar>

			<filter-section
				:show="showFilters"
				:filters="searchParams"
				:search-term="searchField"
				:tenants="tenants"
				date-label="Date Added"
				show-tenant-filter
				show-date-filter
				show-search-filter
				search-label="Reference"
				@update:filters="searchParams = $event"
				@update:search-term="searchField = $event"
				@tenant-change="$global.setSelectedTenant"
			/>

			<div class="flex items-center justify-center pb-4 md:pb-0">
				<div
					class="relative flex w-full flex-wrap gap-2 self-center rounded-lg p-0.5 md:w-[800px] md:flex-nowrap"
				>
					<button
						v-for="option in statusTabs"
						:key="option.value"
						type="button"
						class="relative w-[calc(50%-0.25rem)] cursor-default whitespace-nowrap rounded-full border border-main px-4 py-2 text-sm font-medium transition-all active:scale-[0.98] dark:border-main md:w-1/4"
						:class="{
							'border-transparent bg-main text-white dark:border-transparent dark:bg-accentMain dark:text-black':
								activeTab === option.value,
							'bg-transparent text-black hover:bg-main/10 dark:text-light dark:hover:bg-offwhite/10':
								activeTab !== option.value
						}"
						@click="setActiveTab(option.value)"
					>
						<span class="hidden sm:block">{{ option.label }}</span>
						<span class="block sm:hidden">{{ option.shortLabel || option.label }}</span>
					</button>
				</div>
			</div>

			<data-view
				:columns="columns"
				:label="'Porting Requests'"
				:sort-column="sortColumn"
				:reverse="reverse"
				:loading="loading"
				:total-items="Number(totalItems)"
				:page-size="pageSize"
				:current-page="currentPage"
				:pages="pages"
				@sort="setSort"
				@update:page-size="changePageSize"
				@page-change="changePage"
			>
				<template #table-view>
					<generic-table
						:items="items"
						:columns="columns"
						item-type="porting"
						base-route="porting"
						:can-edit="$global.hasUserRole(userRoles, 'porting:update')"
						:view-mode="'table'"
						:empty-state-config="{
							icon: 'fa-light fa-arrows-rotate',
							title: 'No porting requests yet',
							description: 'Start by creating your first number porting request.',
							action: $global.hasUserRole(userRoles, 'porting:create')
								? {
										label: 'Create Porting Request',
										icon: 'fa-light fa-plus',
										handler: () => routeTo('/porting/new')
									}
								: null
						}"
					>
						<template #mobile-view="{ item }">
							<div class="flex w-full flex-col">
								<div class="text-md absolute right-4 top-4 block max-w-[160px]">
									<p class="text-right font-semibold uppercase text-gray-400">Status</p>
									<p class="truncate text-right capitalize">{{ formatStatus(item.status) }}</p>
								</div>
								<div class="flex flex-col gap-2">
									<div class="flex flex-col">
										<p class="font-semibold uppercase text-gray-500">Reference:</p>
										<p>{{ item.ref }}</p>
									</div>
									<div class="flex flex-col">
										<p class="font-semibold uppercase text-gray-500">Name:</p>
										<p>
											{{ item.first_name }} {{ item.last_name
											}}{{ item.company_name ? ` (${item.company_name})` : '' }}
										</p>
									</div>
									<div class="flex flex-col">
										<p class="font-semibold uppercase text-gray-500">Mobile Number:</p>
										<p>{{ item.mbn }}</p>
									</div>
									<div class="flex flex-col">
										<p class="font-semibold uppercase text-gray-500">Requested Date:</p>
										<p>
											{{
												item.requested_date_display === 'ASAP'
													? 'ASAP'
													: $global.dateFormat(item.requested_date || item.date)
											}}
										</p>
									</div>
								</div>
							</div>
						</template>
					</generic-table>
				</template>
			</data-view>

			<table-header
				v-if="!loading && pages.length > 1"
				label="Porting Requests"
				:count="Number(totalItems)"
				:page-size="pageSize"
				:current-page="currentPage"
				:pages="pages"
				:page-sizes="[10, 25, 100]"
				@update:page-size="changePageSize"
				@page-change="changePage"
			/>

			<PortingWizard
				title="Port a Number"
				:open="showPortingModal"
				:user-roles="userRoles"
				:tenants="tenants"
				:accounts-prop="accounts"
				:initial-data="porting"
				@update:open="showPortingModal = $event"
				@complete="handlePortingComplete"
			/>
		</div>
	</div>
</template>

<script>
import { router } from '../router'
import moment from 'moment'
import debounce from 'lodash.debounce'
import axios from 'axios'
import numberportsApi from '../scripts/numberports/numberports.api'
import accountsApi from '../scripts/accounts/accounts.api'
import PortingWizard from '../components/common/PortingWizard.vue'

export default {
	components: {
		PortingWizard
	},
	inject: ['user', 'userRoles', 'tenants'],
	data() {
		return {
			columns: [
				{
					key: 'ref',
					label: 'Reference',
					sortable: true,
					class: 'col-xs-12 col-sm-2 col-md-2 col-lg-2',
					mobileLabel: 'Reference'
				},
				{
					key: 'name',
					label: 'Name',
					sortable: true,
					class: 'col-xs-12 col-sm-3 col-md-3 col-lg-3',
					mobileLabel: 'Name',
					getValue: (item) => {
						const name = `${item.first_name || ''} ${item.last_name || ''}`.trim()
						return item.company_name ? `${name} (${item.company_name})` : name
					}
				},
				{
					key: 'mbn',
					label: 'MBN',
					sortable: true,
					class: 'col-xs-12 col-sm-2 col-md-2 col-lg-2',
					mobileLabel: 'MBN'
				},
				{
					key: 'requested_date',
					label: 'Requested Date',
					sortable: true,
					class: 'col-xs-12 col-sm-3 col-md-3 col-lg-3',
					mobileLabel: 'Date',
					getValue: (item) => {
						if (item.requested_date_display === 'ASAP') {
							return 'ASAP'
						}
						return this.$global.dateFormat(item.requested_date || item.date)
					}
				},
				{
					key: 'status',
					label: 'Status',
					sortable: true,
					class: 'col-xs-12 col-sm-2 col-md-2 col-lg-2 capitalize',
					mobileLabel: 'Status',
					getValue: (item) => this.formatStatus(item.status)
				}
			],
			showFilters: true,
			viewMode: 'table',
			searchParams: {
				dateAddedFrom: null,
				dateAddedTo: null,
				selectedTenant: ''
			},
			items: [],
			totalItems: 0,
			pageSize: 25,
			currentPage: 1,
			sortColumn: 'ref',
			reverse: false,
			loading: false,
			searchField: '',
			activeTab: 'all',
			statusTabs: [
				{ value: 'all', label: 'All Requests', shortLabel: 'All' },
				{ value: 'received', label: 'Received', shortLabel: 'Received' },
				{ value: 'accepted', label: 'Accepted', shortLabel: 'Accepted' },
				{ value: 'rejected', label: 'Rejected', shortLabel: 'Rejected' },
				{ value: 'complete', label: 'Complete', shortLabel: 'Complete' }
			],
			showPortingModal: false,
			associatedNumbersText: '',
			porting: {
				number: '',
				currentProvider: '',
				account_number: '',
				title: 'Mr',
				first_name: '',
				last_name: '',
				company_name: '',
				property_number: '',
				street: '',
				town: '',
				postcode: '',
				authorisedPerson: '',
				businessName: '',
				pin: '',
				authorised: false,
				selectedTenant: '',
				app: '',
				lcp: '',
				rh: '',
				lines: 1,
				channels: 1,
				multi_line: false,
				comments: '',
				associated_numbers: []
			},
			allAccounts: []
		}
	},

	computed: {
		pages() {
			const numPages = Math.ceil(this.totalItems / this.pageSize) || 1
			return Array.from({ length: numPages }, (_, i) => i)
		},
		accounts() {
			if (!this.porting.selectedTenant) return this.allAccounts
			return this.allAccounts
				.filter((account) => account.tenantId === parseInt(this.porting.selectedTenant))
				.sort((a, b) => a.name.localeCompare(b.name))
		}
	},

	watch: {
		searchParams: {
			handler(newParams) {
				if (newParams.selectedTenant !== undefined) {
					this.porting.selectedTenant = newParams.selectedTenant
				}
				this.reloadData()
			},
			deep: true
		},
		searchField(newValue, oldValue) {
			this.debouncedWatch(newValue, oldValue)
		},
		currentPage() {
			this.reloadData()
		},
		'$route.query': {
			handler(newQuery) {
				const tab = newQuery.tab
				if (tab && this.statusTabs.some((t) => t.value === tab)) {
					this.activeTab = tab
				}
			},
			deep: true,
			immediate: true
		},
		'porting.currentProvider': function (val) {
			this.porting.lcp = val
		},
		'porting.businessName': function (val) {
			this.porting.rh = val
		}
	},

	async created() {
		this.debouncedWatch = debounce(this.reloadData, 1000)

		const tab = this.$route.query.tab
		if (tab && this.statusTabs.some((t) => t.value === tab)) {
			this.activeTab = tab
		}

		if (this.$global.getSelectedTenant()) {
			this.searchParams.selectedTenant = this.$global.getSelectedTenant()
			this.porting.selectedTenant = this.$global.getSelectedTenant()
		}

		await this.loadAccounts()

		if (this.$route.query?.globalSearch) {
			this.reloadData(this.$route.query.globalSearch)
		} else {
			this.reloadData()
		}

		if (this.$route.query.resubmit === 'true') {
			const resubmitData = sessionStorage.getItem('portingResubmitData')
			if (resubmitData) {
				try {
					const parsedData = JSON.parse(resubmitData)
					this.openPortingModal(parsedData)

					sessionStorage.removeItem('portingResubmitData')

					this.$router.replace({
						query: Object.fromEntries(
							Object.entries(this.$route.query).filter(([key]) => key !== 'resubmit')
						)
					})
				} catch (error) {
					console.error('Error parsing resubmit data:', error)
				}
			}
		}
	},

	beforeUnmount() {
		this.debouncedWatch.cancel()
	},

	methods: {
		resetSearchParams() {
			this.searchParams = {
				dateAddedFrom: null,
				dateAddedTo: null,
				selectedTenant: ''
			}
			this.searchField = ''
		},

		setActiveTab(tab) {
			this.activeTab = tab
			this.$router.push({ query: { ...this.$route.query, tab } })
			this.reloadData()
		},

		getSearchParams() {
			const params = {
				limit: this.pageSize,
				skip: this.pageSize * (this.currentPage - 1),
				where: {},
				order: {
					[this.sortColumn]: this.reverse ? 'DESC' : 'ASC'
				}
			}

			if (this.searchParams.dateAddedFrom) {
				params.where.createdAtFrom = `${moment(this.searchParams.dateAddedFrom).format('YYYY-MM-DD')} 00:00:00`
				params.where.createdAtTo = this.searchParams.dateAddedTo
					? `${moment(this.searchParams.dateAddedTo).format('YYYY-MM-DD')} 23:59:59`
					: `${moment(this.searchParams.dateAddedFrom).format('YYYY-MM-DD')} 23:59:59`
			}

			if (this.searchParams.selectedTenant) {
				params.where.selectedTenant = this.searchParams.selectedTenant
			}

			if (this.activeTab && this.activeTab !== 'all') {
				params.where.status = this.activeTab
			}

			return params
		},

		async reloadData(globalSearch) {
			try {
				this.loading = true
				let params = this.getSearchParams()

				if (globalSearch) {
					params = { ...params, searchTerm: globalSearch }
				} else if (this.searchField) {
					params.searchTerm = this.searchField
				}

				const response = await numberportsApi.getAll(params)

				this.items = Array.isArray(response.data.results) ? response.data.results : []
				this.totalItems = typeof response.data.count === 'number' ? response.data.count : 0
			} catch (err) {
				console.error(err)
				this.$swal({
					title: 'Error',
					text: 'Failed to load porting requests',
					icon: 'error'
				})
				this.items = []
				this.totalItems = 0
			} finally {
				this.loading = false
			}
		},

		handleAddNew() {
			this.openPortingModal()
		},

		async openPortingModal(data) {
			this.showPortingModal = true
			if (data) {
				this.porting = { ...this.porting, ...data }
			}
		},

		routeTo(routeName) {
			router.push(routeName)
		},

		setSort(column) {
			if (this.sortColumn === column) {
				this.reverse = !this.reverse
			} else {
				this.sortColumn = column
				this.reverse = false
			}
			this.reloadData()
		},

		changePage(page) {
			this.currentPage = page + 1
		},

		changePageSize(size) {
			this.pageSize = size
			this.currentPage = 1
			this.reloadData()
		},

		async exportToCsv() {
			try {
				const searchParams = { ...this.getSearchParams(), exportCsv: true }
				const response = await axios.get(`${window.dtServerUrl}/numberports`, { params: searchParams })

				const blob = new Blob([response.data], { type: response.headers.get('content-type') })
				const link = document.createElement('a')
				link.href = window.URL.createObjectURL(blob)
				link.download = `porting-requests-${new Date().toISOString().slice(0, 10)}.csv`
				link.click()
			} catch (error) {
				console.error(error)
				this.$swal({
					title: 'Export Failed',
					text: 'Failed to export porting requests data',
					icon: 'error'
				})
			}
		},

		getTenantName(tenantId) {
			if (!tenantId) return 'Not selected'
			const tenant = this.tenants?.find((t) => t.id === parseInt(tenantId))
			return tenant ? tenant.companyName : 'Unknown'
		},

		handlePortingComplete() {
			this.$swal({
				title: 'Port Request Submitted',
				text: `Your port request has now been submitted for validation, we will update you on progress in due course.`,
				icon: 'success'
			})
			this.reloadData()
		},

		updateAssociatedNumbers() {
			this.porting.associated_numbers = this.associatedNumbersText
				.split('\n')
				.map((line) => line.trim())
				.filter((line) => line !== '')
		},

		updateFromFields() {
			this.porting.lcp = this.porting.currentProvider
			this.porting.rh = this.porting.businessName
		},

		resetPortingForm() {
			this.porting = {
				number: '',
				currentProvider: '',
				account_number: '',
				title: 'Mr',
				first_name: '',
				last_name: '',
				company_name: '',
				property_number: '',
				street: '',
				town: '',
				postcode: '',
				authorisedPerson: '',
				businessName: '',
				pin: '',
				authorised: false,
				selectedTenant: this.searchParams.selectedTenant || '',
				app: '',
				lcp: '',
				rh: '',
				lines: 1,
				channels: 1,
				multi_line: false,
				comments: '',
				associated_numbers: []
			}
			this.associatedNumbersText = ''
		},

		async loadAccounts() {
			try {
				const result = await accountsApi.getAll({ limit: 2500 })
				this.allAccounts = result.data.results.sort((a, b) => a.name.localeCompare(b.name))
			} catch (error) {
				console.error('Error loading accounts:', error)
				this.allAccounts = []
			}
		},

		formatStatus(status) {
			const statusMap = {
				received: 'Received',
				accepted: 'Accepted',
				rejected: 'Rejected',
				complete: 'Complete'
			}
			return statusMap[status] || status
		}
	}
}
</script>

<style>
.v-modal-container {
	max-height: 90vh !important;
	transition: opacity 0.3s ease;
}
.v-modal-content {
	max-height: calc(90vh - 120px) !important;
	overflow-y: auto;
}

body.swal2-shown .swal2-popup {
	width: 28rem !important;
	max-width: 90vw !important;
}

@media (max-width: 500px) {
	body.swal2-shown .swal2-actions {
		flex-direction: column !important;
		gap: 0.5rem !important;
	}

	body.swal2-shown .swal2-actions button {
		margin: 0 !important;
	}
}
</style>
